import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { get } from '../../helpers/api_helper';
import WarehouseMapDesignerCanvas from './WarehouseMapDesignerCanvas';

const NOOP = () => {};

const lightShell = {
  background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)',
  border: '1px solid rgba(148, 163, 184, 0.2)',
  boxShadow: '0 10px 24px rgba(15, 23, 42, 0.05)',
  title: '#334155',
};

const darkShell = {
  background: 'linear-gradient(180deg, rgba(59, 61, 54, 0.98) 0%, rgba(48, 50, 44, 0.96) 100%)',
  border: '1px solid rgba(109, 112, 101, 0.38)',
  boxShadow: '0 16px 32px rgba(0, 0, 0, 0.2)',
  title: '#f3efe4',
};

function toLocationLabel(location) {
  if (typeof location === 'string') return location.trim();
  if (typeof location?.label === 'string') return location.label.trim();
  return '';
}

function toLocationSequence(location, index) {
  const sequence = Number(location?.sequence);
  return Number.isInteger(sequence) && sequence > 0 ? sequence : index + 1;
}

function matchesAxisRule(entry, label) {
  const patterns = Array.isArray(entry.pattern) ? entry.pattern : [entry.pattern];
  return patterns.some((pattern) => {
    if (!pattern) return false;
    try {
      return new RegExp(pattern, 'i').test(label);
    } catch (error) {
      return false;
    }
  });
}

function resolveLocationTargetCells(definition, label) {
  if (!label) return [];
  const rows = definition.rows.filter((row) => matchesAxisRule(row, label));
  const columns = definition.columns.filter((column) => matchesAxisRule(column, label));

  return rows.flatMap((row) => columns.map((column) => ({
    columnId: column.id,
    rowId: row.id,
  })));
}

function getHeatmapColor(value, maxValue) {
  if (!value || value <= 0 || !maxValue || maxValue <= 0) return null;
  const normalized = Math.log(value + 1) / Math.log(maxValue + 1);
  const hue = 55 * (1 - normalized);
  const lightness = 92 - (normalized * 44);
  return `hsl(${hue}, 92%, ${lightness}%)`;
}

function getHeatmapDateRange(startDate, endDate) {
  const datePattern = /^\d{4}-\d{2}-\d{2}$/;
  if (!datePattern.test(startDate || '') || !datePattern.test(endDate || '')) return [];

  const start = new Date(`${startDate}T00:00:00Z`);
  const end = new Date(`${endDate}T00:00:00Z`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) return [];

  const dates = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    dates.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return dates;
}

function rowsToHighlights(rows) {
  return rows.map((row) => ({
    label: toLocationLabel(row?.label),
    opacity: 1,
    value: Number(row?.value || 0),
  })).filter((row) => row.label && row.value > 0);
}

function compileHeatmapFrames(rows, startDate, endDate, animationWeekday = '') {
  const rangeDates = getHeatmapDateRange(startDate, endDate);
  const rangeDateSet = new Set(rangeDates);
  const selectedWeekday = animationWeekday === '' ? null : Number(animationWeekday);
  const dates = rangeDates.filter((date) => {
    const weekday = new Date(`${date}T00:00:00Z`).getUTCDay();
    return weekday >= 1 && weekday <= 5
      && (selectedWeekday === null || weekday === selectedWeekday);
  });
  const rowsByDate = new Map(dates.map((date) => [date, []]));
  const summaryByLabel = new Map();

  rows.forEach((row) => {
    const date = String(row?.date || '').slice(0, 10);
    const label = toLocationLabel(row?.label);
    const value = Number(row?.value || 0);
    if (!rangeDateSet.has(date) || !label || value <= 0) return;

    summaryByLabel.set(label, (summaryByLabel.get(label) || 0) + value);
    if (rowsByDate.has(date)) rowsByDate.get(date).push({ label, value });
  });

  return {
    frames: dates.map((date) => ({
      date,
      highlights: rowsToHighlights(rowsByDate.get(date)),
    })),
    summaryHighlights: rowsToHighlights(
      Array.from(summaryByLabel, ([label, value]) => ({ label, value })),
    ),
  };
}

function blendHeatmapHighlights(previousHighlights, currentHighlights, progress) {
  if (!previousHighlights || progress >= 1) return currentHighlights;
  const previousByLabel = new Map(previousHighlights.map((item) => [item.label, item]));
  const currentByLabel = new Map(currentHighlights.map((item) => [item.label, item]));
  const labels = new Set([...previousByLabel.keys(), ...currentByLabel.keys()]);

  return Array.from(labels).map((label) => {
    const previous = previousByLabel.get(label);
    const current = currentByLabel.get(label);
    if (!previous) return { ...current, opacity: progress };
    if (!current) return { ...previous, opacity: 1 - progress };
    return {
      label,
      opacity: 1,
      value: previous.value + ((current.value - previous.value) * progress),
    };
  });
}

function resolveHeatmapCells(definition, highlights) {
  const totals = new Map();
  highlights.forEach((highlight) => {
    resolveLocationTargetCells(definition, highlight.label).forEach((cell) => {
      const key = `${cell.rowId}:${cell.columnId}`;
      const value = highlight.value * (highlight.opacity ?? 1);
      const current = totals.get(key) || { cell, value: 0 };
      current.value += value;
      totals.set(key, current);
    });
  });

  const maxValue = Array.from(totals.values()).reduce(
    (maximum, entry) => Math.max(maximum, entry.value),
    0,
  );

  return Array.from(totals.values()).map((entry) => ({
    color: getHeatmapColor(entry.value, maxValue),
    opacity: 0.9,
    targetCells: [entry.cell],
  }));
}

function resolveGenericHighlights(definition, highlights) {
  return highlights.map((highlight) => ({
    color: highlight.color,
    opacity: highlight.opacity,
    targetCells: resolveLocationTargetCells(definition, toLocationLabel(highlight)),
  })).filter((highlight) => highlight.color && highlight.targetCells.length > 0);
}

function formatHeatmapDate(value, isEndDate = false) {
  if (!value) return null;
  if (value.includes('T')) return value;
  return isEndDate ? `${value}T23:59:59` : `${value}T00:00:00`;
}

function formatDisplayDate(value) {
  if (!value) return '';
  return new Date(`${value}T00:00:00`).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function WarehouseMapCanvas({
  animationWeekday,
  definition,
  endDate,
  fillAvailable,
  highlights,
  layoutModeType,
  locations,
  mode,
  routeSegments,
  routeType,
  slideDurationMicroseconds,
  startDate,
  title,
}) {
  const [loadedDefinition, setLoadedDefinition] = useState(null);
  const [mapLoading, setMapLoading] = useState(!definition);
  const [mapLoadError, setMapLoadError] = useState(false);
  const [routePreviewIndex, setRoutePreviewIndex] = useState(-1);
  const [heatmapRows, setHeatmapRows] = useState([]);
  const [heatmapFrames, setHeatmapFrames] = useState([]);
  const [heatmapSummaryHighlights, setHeatmapSummaryHighlights] = useState([]);
  const [heatmapLoading, setHeatmapLoading] = useState(false);
  const [heatmapLoadError, setHeatmapLoadError] = useState(false);
  const [heatmapView, setHeatmapView] = useState('summary');
  const [heatmapFrameIndex, setHeatmapFrameIndex] = useState(0);
  const [previousHeatmapFrameIndex, setPreviousHeatmapFrameIndex] = useState(null);
  const [heatmapTransitionProgress, setHeatmapTransitionProgress] = useState(1);
  const heatmapFrameIndexRef = useRef(0);
  const activeDefinition = definition || loadedDefinition;
  const shell = layoutModeType === 'dark' ? darkShell : lightShell;

  useEffect(() => {
    if (definition) {
      setMapLoading(false);
      setMapLoadError(false);
      return undefined;
    }

    let cancelled = false;
    const loadMap = async () => {
      setMapLoading(true);
      setMapLoadError(false);
      try {
        const map = await get('/api/warehouse/map');
        if (!cancelled) setLoadedDefinition(map.definition);
      } catch (error) {
        if (!cancelled) setMapLoadError(true);
      } finally {
        if (!cancelled) setMapLoading(false);
      }
    };
    loadMap();
    return () => { cancelled = true; };
  }, [definition]);

  const routeStops = useMemo(() => {
    if (!activeDefinition || mode === 'heatmap') return [];
    return locations.map((location, index) => {
      const label = toLocationLabel(location);
      return {
        locations: [label],
        sequence: toLocationSequence(location, index),
        targetCells: resolveLocationTargetCells(activeDefinition, label),
      };
    }).filter((stop) => stop.locations[0] && stop.targetCells.length > 0);
  }, [activeDefinition, locations, mode]);
  const routeStopKey = routeStops.map((stop) => (
    `${stop.sequence}:${stop.locations[0]}`
  )).join('|');

  useEffect(() => {
    if (mode === 'heatmap' || routeStops.length === 0) {
      setRoutePreviewIndex(-1);
      return undefined;
    }
    if (routeStops.length === 1) {
      setRoutePreviewIndex(0);
      return undefined;
    }

    let frameId = null;
    let timeoutId = null;
    const durationMs = Math.max(1800, routeStops.length * 140);
    const runCycle = () => {
      const startedAt = performance.now();
      setRoutePreviewIndex(0);
      const tick = (now) => {
        const progress = Math.min(1, (now - startedAt) / durationMs);
        setRoutePreviewIndex(Math.min(
          routeStops.length - 1,
          Math.floor(progress * routeStops.length),
        ));
        if (progress < 1) {
          frameId = window.requestAnimationFrame(tick);
        } else {
          timeoutId = window.setTimeout(runCycle, 5000);
        }
      };
      frameId = window.requestAnimationFrame(tick);
    };
    runCycle();
    return () => {
      if (frameId !== null) window.cancelAnimationFrame(frameId);
      if (timeoutId !== null) window.clearTimeout(timeoutId);
    };
  }, [mode, routeStopKey, routeStops.length]);

  useEffect(() => {
    if (mode !== 'heatmap') return undefined;
    const formattedStartDate = formatHeatmapDate(startDate);
    const formattedEndDate = formatHeatmapDate(endDate, true);
    if (!formattedStartDate || !formattedEndDate) {
      setHeatmapRows([]);
      return undefined;
    }

    let cancelled = false;
    const loadHeatmap = async () => {
      setHeatmapLoading(true);
      setHeatmapLoadError(false);
      try {
        const params = new URLSearchParams({
          end_date: formattedEndDate,
          group_by_day: 'true',
          start_date: formattedStartDate,
        });
        if (routeType) params.set('route_type', routeType);
        const data = await get(`/api/warehouse/pick/heatmap?${params.toString()}`);
        if (!cancelled) setHeatmapRows(Array.isArray(data) ? data : []);
      } catch (error) {
        if (!cancelled) {
          setHeatmapRows([]);
          setHeatmapLoadError(true);
        }
      } finally {
        if (!cancelled) setHeatmapLoading(false);
      }
    };
    loadHeatmap();
    return () => { cancelled = true; };
  }, [endDate, mode, routeType, startDate]);

  useEffect(() => {
    if (mode !== 'heatmap' || heatmapLoadError) return;
    const compiled = compileHeatmapFrames(
      heatmapRows,
      startDate,
      endDate,
      animationWeekday,
    );
    setHeatmapFrames(compiled.frames);
    setHeatmapSummaryHighlights(compiled.summaryHighlights);
    setHeatmapView('summary');
    setHeatmapFrameIndex(0);
    heatmapFrameIndexRef.current = 0;
    setPreviousHeatmapFrameIndex(null);
    setHeatmapTransitionProgress(1);
  }, [animationWeekday, endDate, heatmapLoadError, heatmapRows, mode, startDate]);

  useEffect(() => {
    if (mode !== 'heatmap' || heatmapView !== 'playing' || heatmapFrames.length === 0) {
      return undefined;
    }

    let frameId = null;
    let timeoutId = null;
    let cancelled = false;
    const durationMs = Math.max(1, Number(slideDurationMicroseconds) / 1000);
    const fadeDurationMs = Math.min(durationMs, Math.max(100, Math.min(400, durationMs * 0.35)));
    const scheduleNextFrame = (delayMs) => {
      timeoutId = window.setTimeout(() => {
        const previousIndex = heatmapFrameIndexRef.current;
        if (previousIndex >= heatmapFrames.length - 1) {
          setHeatmapView('finished');
          return;
        }
        const nextIndex = previousIndex + 1;
        const transitionStart = performance.now();
        heatmapFrameIndexRef.current = nextIndex;
        setPreviousHeatmapFrameIndex(previousIndex);
        setHeatmapFrameIndex(nextIndex);
        setHeatmapTransitionProgress(0);

        const fade = (now) => {
          if (cancelled) return;
          const progress = Math.min(1, (now - transitionStart) / fadeDurationMs);
          setHeatmapTransitionProgress(progress);
          if (progress < 1) {
            frameId = window.requestAnimationFrame(fade);
          } else {
            setPreviousHeatmapFrameIndex(null);
            scheduleNextFrame(Math.max(0, durationMs - fadeDurationMs));
          }
        };
        frameId = window.requestAnimationFrame(fade);
      }, delayMs);
    };
    scheduleNextFrame(durationMs);
    return () => {
      cancelled = true;
      if (frameId !== null) window.cancelAnimationFrame(frameId);
      if (timeoutId !== null) window.clearTimeout(timeoutId);
    };
  }, [heatmapFrames.length, heatmapView, mode, slideDurationMicroseconds]);

  const currentHeatmapFrame = heatmapFrames[heatmapFrameIndex] || null;
  const previousHeatmapFrame = previousHeatmapFrameIndex === null
    ? null
    : heatmapFrames[previousHeatmapFrameIndex] || null;
  const activeHeatmapHighlights = useMemo(() => {
    if (heatmapView === 'summary') return heatmapSummaryHighlights;
    return blendHeatmapHighlights(
      previousHeatmapFrame?.highlights,
      currentHeatmapFrame?.highlights || [],
      heatmapTransitionProgress,
    );
  }, [
    currentHeatmapFrame,
    heatmapSummaryHighlights,
    heatmapTransitionProgress,
    heatmapView,
    previousHeatmapFrame,
  ]);
  const cellHighlights = useMemo(() => {
    if (!activeDefinition) return [];
    if (mode === 'heatmap') {
      return resolveHeatmapCells(activeDefinition, activeHeatmapHighlights);
    }
    return resolveGenericHighlights(activeDefinition, highlights);
  }, [activeDefinition, activeHeatmapHighlights, highlights, mode]);

  const handleHeatmapPlaybackToggle = () => {
    if (heatmapView === 'playing') {
      setHeatmapView('paused');
      return;
    }
    if (heatmapView === 'summary' || heatmapView === 'finished') {
      heatmapFrameIndexRef.current = 0;
      setHeatmapFrameIndex(0);
      setPreviousHeatmapFrameIndex(null);
      setHeatmapTransitionProgress(1);
    }
    setHeatmapView('playing');
  };

  const handleShowHeatmapTotal = () => {
    setHeatmapView('summary');
    setPreviousHeatmapFrameIndex(null);
    setHeatmapTransitionProgress(1);
  };

  let playbackButtonText = 'Play';
  if (heatmapView === 'playing') playbackButtonText = 'Pause';
  if (heatmapView === 'paused') playbackButtonText = 'Resume';
  if (heatmapView === 'finished') playbackButtonText = 'Replay';

  let description = 'Saved warehouse layout and routed stop order.';
  if (mode === 'heatmap') description = 'Total pick density for the selected date range.';
  if (mode === 'heatmap' && heatmapLoading) description = 'Downloading pick density...';
  if (mode === 'heatmap' && heatmapLoadError) description = 'Could not load pick density for this range.';
  if (mode === 'heatmap' && heatmapView !== 'summary' && currentHeatmapFrame) {
    description = `Day ${heatmapFrameIndex + 1} of ${heatmapFrames.length}`;
  }

  return (
    <div
      className="rounded-3 p-3"
      style={{
        background: shell.background,
        border: shell.border,
        boxShadow: shell.boxShadow,
      }}
    >
      <div className="d-flex justify-content-between align-items-start gap-3 mb-2 flex-wrap">
        <div>
          <div className="text-uppercase text-muted" style={{ letterSpacing: '0.08em', fontSize: '0.65rem' }}>
            {title}
          </div>
          <div className="fw-semibold" style={{ color: shell.title }}>
            {mode === 'heatmap' ? 'Pick Heat Map' : 'Route Map'}
          </div>
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
          {mode === 'heatmap' && heatmapView !== 'summary' && currentHeatmapFrame && (
            <span className="fw-semibold px-2 py-1 rounded">
              {formatDisplayDate(currentHeatmapFrame.date)}
            </span>
          )}
          <span className="small text-muted text-end">{description}</span>
          {mode === 'heatmap' && (
            <div className="btn-group btn-group-sm" role="group" aria-label="Heat map animation controls">
              <button
                type="button"
                className="btn btn-primary"
                disabled={heatmapLoading || heatmapFrames.length === 0}
                onClick={handleHeatmapPlaybackToggle}
              >
                <i className={`mdi ${heatmapView === 'playing' ? 'mdi-pause' : 'mdi-play'} me-1`} />
                {playbackButtonText}
              </button>
              {heatmapView !== 'summary' && (
                <button type="button" className="btn btn-outline-secondary" onClick={handleShowHeatmapTotal}>
                  Show total
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {mapLoading && <div className="text-muted py-4 text-center">Loading warehouse map...</div>}
      {mapLoadError && <div className="text-danger py-4 text-center">Could not load the warehouse map.</div>}
      {activeDefinition && (
        <WarehouseMapDesignerCanvas
          cellHighlights={cellHighlights}
          definition={activeDefinition}
          fillAvailable={fillAvailable}
          interactive={false}
          onCreateAnnotation={NOOP}
          onPaintCell={NOOP}
          onSelectAnnotation={NOOP}
          onSelectAxis={NOOP}
          onSetRouteHome={NOOP}
          routePreviewIndex={routePreviewIndex}
          routePreviewSegments={routeSegments}
          routePreviewStops={routeStops}
          tool="select"
        />
      )}
    </div>
  );
}

const locationPropType = PropTypes.oneOfType([
  PropTypes.string,
  PropTypes.shape({
    label: PropTypes.string,
    sequence: PropTypes.number,
  }),
]);

WarehouseMapCanvas.propTypes = {
  animationWeekday: PropTypes.oneOf(['', '1', '2', '3', '4', '5']),
  definition: PropTypes.shape({
    annotations: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string.isRequired })),
    columns: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      pattern: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
      ]),
      size: PropTypes.number,
    })).isRequired,
    rows: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      pattern: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
      ]),
      size: PropTypes.number,
    })).isRequired,
    walkableCells: PropTypes.arrayOf(PropTypes.shape({
      columnId: PropTypes.string.isRequired,
      rowId: PropTypes.string.isRequired,
    })),
  }),
  endDate: PropTypes.string,
  fillAvailable: PropTypes.bool,
  highlights: PropTypes.arrayOf(PropTypes.shape({
    color: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    opacity: PropTypes.number,
  })),
  layoutModeType: PropTypes.string,
  locations: PropTypes.arrayOf(locationPropType),
  mode: PropTypes.oneOf(['route', 'heatmap']),
  routeSegments: PropTypes.arrayOf(PropTypes.shape({
    cells: PropTypes.arrayOf(PropTypes.shape({
      columnId: PropTypes.string.isRequired,
      rowId: PropTypes.string.isRequired,
    })).isRequired,
  })),
  routeType: PropTypes.string,
  slideDurationMicroseconds: PropTypes.number,
  startDate: PropTypes.string,
  title: PropTypes.string,
};

WarehouseMapCanvas.defaultProps = {
  animationWeekday: '',
  definition: null,
  endDate: null,
  fillAvailable: false,
  highlights: [],
  layoutModeType: 'light',
  locations: [],
  mode: 'route',
  routeSegments: [],
  routeType: '',
  slideDurationMicroseconds: 1000000,
  startDate: null,
  title: 'Warehouse Map',
};

export {
  compileHeatmapFrames,
  resolveLocationTargetCells,
};
export default WarehouseMapCanvas;
